import { Css, Icon, IconButton, IconKey, MaxLines, Palette, Tag, useTestIds } from "@homebound/beam";
import { useState } from "react";
import {
  PlanPackageOverviewV2Query,
  PlanPackageOverviewV2_ReadyPlanOptionFragment,
} from "src/routes/libraries/plan-package-v2/components/OverviewTab.generated";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { pluralize } from "src/utils";
import { programDataBathRange, programDataRange } from "src/utils/programData";

type PlanSummaryProps = {
  planPackage: PlanPackageOverviewV2Query["planPackage"];
  elevations: PlanPackageOverviewV2_ReadyPlanOptionFragment[];
};

export function PlanSummary({ planPackage, elevations }: PlanSummaryProps) {
  const tid = useTestIds({}, "planSummary");
  const [index, setIndex] = useState(0);
  const { name, displayCode, description, markets, minProgramData: min, maxProgramData: max } = planPackage;
  const images = elevationImages(planPackage, elevations);
  const image = images[index];
  const stats: [IconKey, string][] = [
    ["connect", `${programDataRange(min, max, "sellableSqft") ?? 0} Sqft`],
    ["bed", `${programDataRange(min, max, "bedrooms") ?? 0} ${pluralize(max.bedrooms ?? 0, "Bed")}`],
    ["bath", `${programDataBathRange(min, max) ?? 0} Bath`],
    ["house", `${programDataRange(min, max, "stories") ?? 0} ${pluralize(max.stories ?? 0, "Story", "Stories")}`],
  ];

  return (
    <div css={Css.df.gap4.aifs.ifSmOrMd.fdc.aiStretch.gap2.$}>
      {/* Using a background image rather than an inline image so that the <img>'s height won't unintentionally cause the container to be really tall */}
      <div
        {...tid.coverPhoto}
        css={
          Css.relative
            .wPx(636)
            .hPx(338)
            .fs0.br12.oh.bgGray200.add("backgroundImage", `url(${image.url})`)
            .add("backgroundSize", "cover")
            .add("backgroundPosition", "center")
            .add("backgroundRepeat", "no-repeat")
            .ifSmOrMd.w100.ha.add("aspectRatio", "636 / 338").$
        }
      >
        {image.isDefault && (
          <div css={Css.absolute.top1.left1.$} {...tid.defaultElevation}>
            <Tag text="Default" type="success" />
          </div>
        )}
        {image.name && (
          <div css={Css.absolute.bottom1.right1.$} {...tid.elevationName}>
            <Tag text={image.name} variant="secondary" />
          </div>
        )}
        {images.length > 1 && (
          <div css={Css.absolute.top0.bottom0.left1.right1.df.aic.jcsb.$}>
            <IconButton
              icon="chevronLeft"
              color={Palette.White}
              label="Previous elevation"
              onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
              {...tid.previousElevation}
            />
            <IconButton
              icon="chevronRight"
              color={Palette.White}
              label="Next elevation"
              onClick={() => setIndex((i) => (i + 1) % images.length)}
              {...tid.nextElevation}
            />
          </div>
        )}
      </div>
      <div css={Css.fg1.df.fdc.gap1.$}>
        <div css={Css.df.aife.pt4.gap1.ifSmOrMd.pt0.$}>
          <h2 css={Css.xl.$} {...tid.name}>
            {name}
          </h2>
          <span css={Css.md.gray700.$} {...tid.displayCode}>
            {displayCode}
          </span>
        </div>
        <div {...tid.markets}>{markets.map((m) => m.name).join(", ")}</div>
        <div css={Css.df.bcGray200.my2.maxw75.ifSmOrMd.maxw100.$} {...tid.stats}>
          {stats.map(([icon, label], i) => (
            <div key={icon} css={Css.fg1.df.fdc.aic.gap1.px3.bcGray200.br.ifSmOrMd.px1.end.if(i === 0).bl.$}>
              <Icon icon={icon} color={Palette.Gray500} />
              <span css={Css.sm.$}>{label}</span>
            </div>
          ))}
        </div>
        {description ? (
          <div css={Css.maxw("90%").ifSmOrMd.maxw100.$} {...tid.description}>
            <MaxLines maxLines={4}>{description}</MaxLines>
          </div>
        ) : (
          <div css={Css.pt1.$}> No description added. </div>
        )}
      </div>
    </div>
  );
}

type ElevationImage = { url: string; name?: string; isDefault?: boolean };

/** The elevations' images, falling back to the plan's cover photo when none of them have one. */
function elevationImages(
  planPackage: PlanPackageOverviewV2Query["planPackage"],
  elevations: PlanPackageOverviewV2_ReadyPlanOptionFragment[],
): ElevationImage[] {
  const images = elevations
    .map((e, i) => {
      const url = e.optionImage?.previewUrl;
      return url ? { url, name: e.name, isDefault: i === 0 } : undefined;
    })
    .compact();
  if (images.nonEmpty) return images;
  const coverPhoto = planPackage.assets.find((rpa) => rpa.active && rpa.isCoverPhoto);
  return [{ url: coverPhoto?.asset.previewUrl ?? PLAN_FALLBACK_IMG }];
}
